import { create } from 'zustand';
import type { DirectMessage, DmConversation } from '@etown/shared';
import { CHAT_BUFFER_LIMIT } from '../lib/constants';

interface DmState {
  /** Eigene userId — um eingehende von ausgehenden Nachrichten zu trennen. */
  selfUserId: string | null;
  conversations: DmConversation[];
  activePeerId: string | null;
  /** Name des Gegenuebers, wenn es noch keine Konversation mit ihm gibt. */
  activePeerName: string | null;
  messagesByPeer: Record<string, DirectMessage[]>;
  hasMoreByPeer: Record<string, boolean>;
  setSelf: (userId: string) => void;
  setConversations: (conversations: DmConversation[]) => void;
  openConversation: (peerId: string, displayName?: string) => void;
  closeConversation: () => void;
  setHistory: (peerId: string, messages: DirectMessage[], hasMore: boolean) => void;
  prependHistory: (peerId: string, older: DirectMessage[], hasMore: boolean) => void;
  /** Selbst gesendete Nachricht sofort anhaengen (Ack). */
  appendOwn: (message: DirectMessage) => void;
  /** Eingehende Nachricht (dm:message). Liefert true, wenn sie die offene
   *  Konversation betrifft (dann sollte der Aufrufer dm:read senden). */
  addIncoming: (message: DirectMessage) => boolean;
  reset: () => void;
}

/** Peer aus Sicht des Empfaengers = der jeweils andere Teilnehmer. */
function peerOf(message: DirectMessage, selfUserId: string | null): string {
  return message.fromUserId === selfUserId ? message.toUserId : message.fromUserId;
}

function appendMessage(state: DmState, peerId: string, message: DirectMessage): Record<string, DirectMessage[]> {
  const existing = state.messagesByPeer[peerId] ?? [];
  return { ...state.messagesByPeer, [peerId]: [...existing.slice(-CHAT_BUFFER_LIMIT + 1), message] };
}

/** Direktnachrichten-Zustand (Konversationsliste + offene Konversation). */
export const useDmStore = create<DmState>((set, get) => ({
  selfUserId: null,
  conversations: [],
  activePeerId: null,
  activePeerName: null,
  messagesByPeer: {},
  hasMoreByPeer: {},

  setSelf: (userId) => set({ selfUserId: userId }),
  setConversations: (conversations) => set({ conversations }),
  openConversation: (peerId, displayName) => set({ activePeerId: peerId, activePeerName: displayName ?? null }),
  closeConversation: () => set({ activePeerId: null, activePeerName: null }),

  setHistory: (peerId, messages, hasMore) =>
    set((state) => ({
      messagesByPeer: { ...state.messagesByPeer, [peerId]: messages },
      hasMoreByPeer: { ...state.hasMoreByPeer, [peerId]: hasMore },
    })),

  prependHistory: (peerId, older, hasMore) =>
    set((state) => ({
      messagesByPeer: { ...state.messagesByPeer, [peerId]: [...older, ...(state.messagesByPeer[peerId] ?? [])] },
      hasMoreByPeer: { ...state.hasMoreByPeer, [peerId]: hasMore },
    })),

  appendOwn: (message) =>
    set((state) => {
      const peerId = peerOf(message, state.selfUserId);
      return { messagesByPeer: appendMessage(state, peerId, message) };
    }),

  addIncoming: (message) => {
    const state = get();
    const peerId = peerOf(message, state.selfUserId);
    const isActive = state.activePeerId === peerId;
    set({
      messagesByPeer: appendMessage(state, peerId, message),
      conversations: bumpConversation(state.conversations, peerId, message, isActive),
    });
    return isActive;
  },

  reset: () =>
    set({
      selfUserId: null,
      conversations: [],
      activePeerId: null,
      activePeerName: null,
      messagesByPeer: {},
      hasMoreByPeer: {},
    }),
}));

/** Konversationsliste nach eingehender Nachricht aktualisieren (Unread + Vorschau). */
function bumpConversation(
  conversations: DmConversation[],
  peerId: string,
  message: DirectMessage,
  isActive: boolean,
): DmConversation[] {
  const existing = conversations.find((c) => c.peerId === peerId);
  const updated: DmConversation = {
    peerId,
    peerDisplayName: existing?.peerDisplayName ?? message.fromDisplayName,
    lastBody: message.body,
    lastAt: message.createdAt,
    lastId: message.id,
    unread: isActive ? 0 : (existing?.unread ?? 0) + 1,
  };
  const rest = conversations.filter((c) => c.peerId !== peerId);
  return [updated, ...rest];
}

/** Summe aller ungelesenen Direktnachrichten (FAB-Badge). */
export function selectDmUnread(state: DmState): number {
  return state.conversations.reduce((sum, c) => sum + c.unread, 0);
}
